import { useCallback, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

import { getApiConfig } from '../../config/apiConfig';
import type { AuthType } from '../../types';
import { getTokenManager } from '../../config/tokenManager';
import {
  type AccessTokenRefreshedEvent,
  buildRoomKey,
  type GetAuthTokens,
  type SetAuthTokens,
  type WebsocketErrorEvent,
  WS_EVENTS,
  WS_NAMESPACES,
  WS_ROOM_ACTIONS,
  type WsRoomType,
} from '@billx/utils';

export interface UseWebsocketProps {
  authType: AuthType;
  getAuthTokens: GetAuthTokens;
  setAuthTokens: SetAuthTokens;
  enabled?: boolean;
}

export const useWebsocket = ({
  authType,
  getAuthTokens,
  setAuthTokens,
  enabled = true,
}: UseWebsocketProps) => {
  const socketRef = useRef<Socket | null>(null);
  const roomRefCount = useRef<
    Map<string, { type: WsRoomType; id: string; count: number }>
  >(new Map());
  const handlers = useRef<Map<string, Set<(payload: unknown) => void>>>(
    new Map()
  );

  useEffect(() => {
    if (!enabled) return;

    const tokens = getAuthTokens();
    if (!tokens.accessToken) return;

    const socket = io(`${getApiConfig().socketUrl}/${WS_NAMESPACES.ORDERS}`, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      auth: {
        ...tokens,
        type: authType,
      },
    });

    socketRef.current = socket;

    socket.on(WS_EVENTS.CONNECTED, () => {
      getTokenManager().logger.logWSEvent(
        'connection',
        `${authType}:socket:connected`,
        {
          socketId: socket?.id,
          authType,
        }
      );

      // Rejoin rooms on reconnect
      for (const [, roomInfo] of roomRefCount.current.entries()) {
        const action = WS_ROOM_ACTIONS[roomInfo.type];
        socket.emit(action.join, { [action.idKey]: roomInfo.id });
      }
    });

    socket.on(WS_EVENTS.ROOM.JOINED, (data) =>
      getTokenManager().logger.logWSEvent('rooms', 'room:joined', {
        socketId: socket?.id,
        ...data,
      })
    );

    socket.on('error', (data: WebsocketErrorEvent) =>
      getTokenManager().logger.logWSError(data.message, { data })
    );

    socket.on('disconnect', () =>
      getTokenManager().logger.logWSEvent(
        'connection',
        `${authType}:socket:disconnected`,
        {
          socketId: socket?.id,
          authType,
        }
      )
    );

    socket.on('token:refreshed', ({ data }: AccessTokenRefreshedEvent) => {
      setAuthTokens(data);
      socket.auth = {
        ...getAuthTokens(),
        type: authType,
      };
      socket.disconnect().connect();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [authType, getAuthTokens, setAuthTokens, enabled]);

  const joinRoom = useCallback((type: WsRoomType, id: string) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;

    const key = buildRoomKey(type, id);
    const existing = roomRefCount.current.get(key);

    if (!existing) {
      const action = WS_ROOM_ACTIONS[type];
      socket.emit(action.join, { [action.idKey]: id });
      roomRefCount.current.set(key, { type, id, count: 1 });
    } else {
      roomRefCount.current.set(key, { ...existing, count: existing.count + 1 });
    }
  }, []);

  const leaveRoom = useCallback((type: WsRoomType, id: string) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;

    const key = buildRoomKey(type, id);
    const existing = roomRefCount.current.get(key);

    if (!existing) return;

    if (existing.count <= 1) {
      const action = WS_ROOM_ACTIONS[type];
      socket.emit(action.leave, { [action.idKey]: id });
      roomRefCount.current.delete(key);
    } else {
      roomRefCount.current.set(key, { ...existing, count: existing.count - 1 });
    }
  }, []);

  const subscribe = useCallback(
    <T = unknown>({
      event,
      handler,
    }: {
      event: string;
      handler: (payload: T) => void;
    }) => {
      const socket = socketRef.current;
      if (!socket) {
        return () => {};
      }

      if (!handlers.current.has(event)) {
        handlers.current.set(event, new Set());
        socket.on(event, (payload: unknown) => {
          handlers.current.get(event)?.forEach((h) => h(payload));
        });
      }
      handlers.current.get(event)!.add(handler as (payload: unknown) => void);

      return () => {
        handlers.current
          .get(event)
          ?.delete(handler as (payload: unknown) => void);
        if (handlers.current.get(event)?.size === 0) {
          socket.off(event);
          handlers.current.delete(event);
        }
      };
    },
    []
  );
  return {
    subscribe,
    joinRoom,
    leaveRoom,
    socket: socketRef.current,
  };
};
